import { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'motion/react'
import { X } from 'lucide-react'
import { TOKENS } from '../ui/dashboard-primitives'

interface MachineVideoOverlayProps {
  src: string
  title: string
  onClose: () => void
}

/**
 * Full-screen player for locally cached machine exercise videos (/media/...).
 * Mirrors VideoPlayerOverlay's chrome and focus handling, but plays a native
 * <video> element instead of the YouTube IFrame API.
 */
export function MachineVideoOverlay({ src, title, onClose }: MachineVideoOverlayProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  const handleClose = useCallback(() => onClose(), [onClose])

  // Focus management: capture previous focus, inert the rest of the page,
  // move focus to the close button, restore on unmount.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null

    const root = document.getElementById('root')
    root?.setAttribute('inert', '')

    const focusTimer = window.setTimeout(() => {
      closeButtonRef.current?.focus()
    }, 60)

    return () => {
      window.clearTimeout(focusTimer)
      root?.removeAttribute('inert')
      requestAnimationFrame(() => {
        previouslyFocusedRef.current?.focus?.()
      })
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [handleClose])

  const overlay = (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-100 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(20, 14, 12, 0.96)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <button
        ref={closeButtonRef}
        type="button"
        onClick={handleClose}
        className="absolute right-4 top-4 sm:right-6 sm:top-6 z-10 flex h-11 w-11 items-center justify-center rounded-full outline-none transition-colors focus-visible:ring-2"
        style={
          {
            backgroundColor: TOKENS.creamHairline,
            border: '1px solid ' + TOKENS.creamBorder,
            color: TOKENS.cream,
            ['--tw-ring-color' as never]: TOKENS.amberEmphasis,
          } as React.CSSProperties
        }
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = TOKENS.creamBorder
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = TOKENS.creamHairline
        }}
        aria-label="Close video"
      >
        <X className="h-5 w-5" aria-hidden />
      </button>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className="mx-4 w-full max-w-[1080px]"
      >
        {/* Exercise demo footage from Technogym ships without caption tracks. */}
        <video
          src={src}
          autoPlay
          controls
          playsInline
          className="h-auto w-full overflow-hidden rounded-xl"
          style={{
            boxShadow:
              `0 40px 80px -30px rgba(20, 14, 12, 0.9), inset 0 0 0 1px ${TOKENS.creamBorder}`,
          }}
        />
      </motion.div>
    </motion.div>
  )

  return createPortal(overlay, document.body)
}
